import React, { useEffect, useMemo, useState } from "react";
import "./Recruitment.css";
import "./RecruitmentPipeline.css";
import config from "../../config";
import { BsSliders } from "react-icons/bs";
import SuccessPopup from "../../components/common/SuccessPopup";
import ErrorPopup from "../../components/common/ErrorPopup";


// Canonical enum values from backend
const getStatusEnums = () => {
  const userEmail = localStorage.getItem("userEmail");
  if (userEmail === "nvguhan@wtilth.com") {
    return ["DISCOVERY", "WTILTH_INTERVIEW", "L1", "L2", "SELECTED", "REJECTED"];
  }
  return [
    "INITIAL",
    "INTERVIEWED",
    "ACCEPTED",
    "REJECTED",
    "ONBOARDED",  
  ];
};

// Pretty label for UI

const truncateText = (text = "", max = 12) => {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "..." : text;
};

const statusLabel = (enumVal) =>
  String(enumVal)
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Normalize any server/UI value to canonical enum
const normalizeStatus = (value) => {
  const currentEnums = getStatusEnums();
  if (!value) return currentEnums[0];

  const up = String(value).trim().toUpperCase();

  const alias = {
    INTERVIEW: "INTERVIEWED",
    INTERVIEWING: "INTERVIEWED",
    ONBOARDING: "ONBOARDED",
    "ON-BOARDED": "ONBOARDED",
    // CANCELED: "CANCELLED",
  };

  const mapped = alias[up] || up;

  return currentEnums.includes(mapped) ? mapped : currentEnums[0];
};


const RecruitmentPipeline = ({ activeMainTab, setActiveMainTab }) => {
  // user context
  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  // clients
  const [clients, setClients] = useState([]); // [{name, clientKey}]
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState(null);
  const [filterRecruitmentId, setFilterRecruitmentId] = useState("");
  const [recruitmentOptions, setRecruitmentOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(""); // name (required)

  // candidates
  const [candidateRows, setCandidateRows] = useState([]); // normalized
  const [loading, setLoading] = useState(true); // candidates loading
  const [error, setError] = useState(null);

  // status tabs
  const [activeStatusTab, setActiveStatusTab] = useState(getStatusEnums()[0]);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  // filter modal state (unchanged)
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterRecruitmentName, setFilterRecruitmentName] = useState("");
  const [filterSortBy, setFilterSortBy] = useState("");

  // bulk status state
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBulkDropdown, setShowBulkDropdown] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");

  // popup state
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // recruitment names for filter


  // Load user details
  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  const statusToUx = (enumVal) =>
    String(enumVal)
      .toLowerCase()
      .split("_")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");


  // Fetch CLIENTS (value to use/display = name)
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchClients = async () => {
      setClientsLoading(true);
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${userDetails.orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );
        if (!res.ok)
          throw new Error(`Failed to fetch clients (HTTP ${res.status})`);
        const data = await res.json();
        const normalized = Array.isArray(data.body)
          ? data.body
            .filter((c) => c && typeof c.name === "string")
            .map((c) => ({ name: c.name, clientKey: c.clientKey }))
          : [];

        setClients(normalized);
        setClientsError(null);

        // default selection to first client if none selected
        if (!selectedClient && normalized.length > 0) {
          setSelectedClient(normalized[0].name);
        }
      } catch (err) {
        console.error("Client fetch error:", err);
        setClients([]);
        setClientsError("Could not load clients.");
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails.token, userDetails.orgId]);

  // Fetch candidates from API (all)
  useEffect(() => {
    if (!userDetails.token) return;

    const fetchCandidates = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          orgKey: userDetails.orgId
        });
        const url = `${config.API_BASE_URL}${config.CANDIDATES_LIST_ENDPOINT}?${params.toString()}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to fetch candidates");

        const page = await response.json(); // Spring Page<Candidate>
        const list = Array.isArray(page?.content) ? page.content : [];

        const toDDMMYYYY = (d) => {
          if (!d) return "";
          try {
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return String(d);
            const dd = String(dt.getDate()).padStart(2, "0");
            const mm = String(dt.getMonth() + 1).padStart(2, "0");
            const yyyy = dt.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          } catch {
            return String(d);
          }
        };

        const normalized = list.map((c, idx) => {
          const normalizedPhone =
            c.phoneNumber || c.phone || c.mobile || "";

          return {
            key: c.candidateKey || c.key || c.id || `C-${idx + 1}`,

            // ===== Basic Details =====
            candidateName: c.name || "",
            name: c.name || "", // table ku use aagum
            emailId: c.email || "",
            email: c.email || "",
            phoneNumber: normalizedPhone,
            dob: c.dob || "",
            gender: c.gender || "",

            // ===== Identity =====
            aadharNumber: c.aadhar || "",
            uanNumber: c.uan || "",

            // ===== Address =====
            address: c.addressLine1 || "",
            city: c.city || "",
            district: c.district || "",
            state: c.state || "",
            country: c.country || "",
            pincode: c.pinCode || "",

            // ===== Client & Recruitment =====
            client: c.clientName || "",
            clientName: c.clientName || c.client?.name || "",
            recruitmentName: c.recruitmentId ? String(c.recruitmentId) : "",
            recruitment_key:
              c.recruitmentKey || c.recruitment?.recruitmentKey || "",

            // ===== Job Details =====
            jobRole: c.jobRole || "",
            role: c.jobRole || "",
            skills: c.skillsConfigured || "",
            qualification: c.qualification || "",
            subVendor: c.subVendorName || "",

            // ===== Interview =====
            interviewDate: c.interviewDate || "",
            interviewTime: c.interviewTime || "",
            pointOfContactInterview: c.pocName || "",
            pocRole: c.pocRole || "",

            // ===== Status & Dates =====
            status: normalizeStatus(c.status),
            createdDate: toDDMMYYYY(
              c.createdDate ||
              c.createdOn ||
              c.created_at ||
              c.createdAt ||
              c.interviewDate
            ),

            // ===== IDs =====
            id: c.id,
          };
        });

        setCandidateRows(normalized);
        setError(null);
      } catch (err) {
        console.error("Candidate fetch error:", err);
        setCandidateRows([]);
        setError("Could not load candidates.");
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [userDetails.token, userDetails.orgId]);

  // Fetch recruitment list for filter dropdown
  useEffect(() => {
    if (!userDetails.token) return;

    const fetchRecruitments = async () => {
      try {
        const params = new URLSearchParams({
          orgKey: userDetails.orgId
        });

        const url = `${config.API_BASE_URL}${config.RECRUITMENT_LIST_ENDPOINT}?${params.toString()}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch recruitments");

        const page = await response.json();
        const list = Array.isArray(page?.content) ? page.content : [];

        const options = list.map(r => ({
          label: r.name || r.recruitmentName,
          value: r.id || r.recruitmentId
        }));

        setRecruitmentOptions(options);

      } catch (err) {
        console.error("Recruitment fetch error:", err);
        setRecruitmentOptions([]);
      }
    };

    fetchRecruitments();
  }, [userDetails.token, userDetails.orgId]);

  // Derived rows: REQUIRE a client selection. Only show that client's candidates.
  const clientFilteredRows = useMemo(() => {
    if (!selectedClient) return []; // <-- no selection -> show nothing
    return candidateRows.filter((row) => row.clientName === selectedClient);
  }, [candidateRows, selectedClient]);

  // Then filter by active status
  const filteredRows = useMemo(
    () =>
      clientFilteredRows.filter(
        (row) => row.status === activeStatusTab || row.forceShow
      ),
    [clientFilteredRows, activeStatusTab]
  );


  // Pagination math
  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pageRows = filteredRows.slice(startIndex, endIndex);
  const showingFrom = totalRows === 0 ? 0 : startIndex + 1;
  const showingTo = endIndex > totalRows ? totalRows : endIndex;

  // Reset page when the selected client or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedClient, activeStatusTab]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    const firstPage = 1;
    const lastPage = totalPages;
    const prevPage = currentPage - 1;
    const nextPage = currentPage + 1;

    pages.push(firstPage);
    if (currentPage > 3) pages.push("dots-left");
    for (let p = prevPage; p <= nextPage; p++) {
      if (p > 1 && p < lastPage) pages.push(p);
    }
    if (currentPage < totalPages - 2) pages.push("dots-right");
    pages.push(lastPage);
    return [...new Set(pages)];
  };

  const goToPage = (p) => {
    if (p < 1) p = 1;
    if (p > totalPages) p = totalPages;
    setCurrentPage(p);
  };

  const handleStatusTabClick = (enumVal) => {
    setActiveStatusTab(enumVal);
  };

  // Toggle single row checkbox
  const toggleRowSelection = (key) => {
    setSelectedRows((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Select / deselect all visible rows
  const toggleSelectAll = () => {
    if (selectedRows.length === pageRows.length && pageRows.length > 0) {
      setSelectedRows([]);
    } else {
      setSelectedRows(pageRows.map((r) => r.key));
    }
  };

  // Bulk status update handler
  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus) { setErrorMsg("Please select a status"); return; }
    if (selectedRows.length === 0) { setErrorMsg("Please select at least one candidate"); return; }
    try {
      await Promise.all(
        selectedRows.map((key) =>
          fetch(`${config.API_BASE_URL}${config.UPDATE_RECRUITMENT_STATUS_ENDPOINT}`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              orgKey: Number(userDetails.orgId),
              candidateKey: key,
              status: bulkStatus,
            }),
          })
        )
      );
      const canonical = normalizeStatus(bulkStatus);
      setCandidateRows((prev) =>
        prev.map((c) =>
          selectedRows.includes(c.key) ? { ...c, status: canonical } : c
        )
      );
      setSelectedRows([]);
      setBulkStatus("");
      setShowBulkDropdown(false);
      setSuccessMsg("Bulk status updated successfully!");
    } catch (err) {
      console.error(err);
      setErrorMsg("Bulk status update failed");
    }
  };

  // Local update status (keeps enums)
  const updateCandidateStatus = async (candidateKey, newStatus) => {
    const canonicalStatus = normalizeStatus(newStatus);

    const requestBody = {
      orgKey: Number(userDetails.orgId),
      candidateKey: candidateKey,
      status: canonicalStatus,
    };

    try {
      const response = await fetch(
        `${config.API_BASE_URL}${config.UPDATE_RECRUITMENT_STATUS_ENDPOINT}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Status update failed");
      }

      // ✅ Read response ONLY ONCE
      const result = await response.json();
      console.log("✅ Status updated:", result);

      // ✅ Update UI
      setCandidateRows(prev =>
        prev.map(c =>
          c.key === candidateKey
            ? { ...c, status: canonicalStatus }
            : c
        )
      );

      // ✅ Switch tab automatically
      setActiveStatusTab(canonicalStatus);

      // ✅ SUCCESS MESSAGE
      setSuccessMsg("Status updated successfully");

    } catch (err) {
      console.error(" Status update failed:", err);
      setErrorMsg("Status update failed");
    }
  };




  const handleApplyFilter = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        orgKey: userDetails.orgId,
      });

      if (filterRecruitmentId) {
        params.append("recruitmentId", filterRecruitmentId);
      }

      if (filterSortBy) {
        params.append("sortBy", filterSortBy);
      }

      const url = `${config.API_BASE_URL}${config.CANDIDATES_LIST_ENDPOINT}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Filter API failed");
      }

      const page = await response.json();
      const list = Array.isArray(page?.content) ? page.content : [];

      const normalized = list.map((c, idx) => ({
        key: c.candidateKey || `C-${idx + 1}`,
        name: c.name || "",
        email: c.email || "",
        role: c.jobRole || "",
        clientName: c.clientName || "",
        status: normalizeStatus(c.status),
        createdDate: c.createdDate || "",
      }));

      setCandidateRows(normalized);
      setShowFilterModal(false);
    } catch (err) {
      console.error("Filter API error:", err);
      setErrorMsg("Filter failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleClearFilter = () => {
    setFilterRecruitmentName("");
    setFilterSortBy("");
    setShowFilterModal(false);
  };

  return (
    <>
      {/* Success / Error Popups */}
      {successMsg && <SuccessPopup message={successMsg} onClose={() => setSuccessMsg("")} />}
      {errorMsg && <ErrorPopup message={errorMsg} onClose={() => setErrorMsg("")} />}

      {/* Row: Client Name dropdown + tab buttons */}
      <div className='top-row'>
        <div className='client-filter'>
          <label className='client-label'>Client Name</label>
          <select
            className='client-select'
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            disabled={clientsLoading || !!clientsError}
          >
            {/* Force a selection; no "All Clients" */}
            <option value='' disabled hidden>
              {clientsLoading
                ? "Loading clients…"
                : clientsError
                  ? "Failed to load clients"
                  : "Select client"}
            </option>
            {!clientsLoading &&
              !clientsError &&
              clients.map((c) => (
                <option key={c.clientKey ?? c.name} value={c.name}>
                  {truncateText(c.name, 12)}
                </option>

              ))}
          </select>
        </div>

        <div className='main-tabs'>
          <button
            className={
              "main-tab-btn " +
              (activeMainTab === "pipeline" ? "active-main-tab" : "")
            }
            onClick={() => setActiveMainTab("pipeline")}
          >
            Recruitment Pipeline
          </button>

          <button
            className={
              "main-tab-btn " +
              (activeMainTab === "list" ? "active-main-tab" : "")
            }
            onClick={() => setActiveMainTab("list")}
          >
            Recruitment List
          </button>
        </div>

        <div></div>
        <div></div>
      </div>

      {/* Status tabs row (built from enums) */}
      <div className='status-row'>
        <div>
          <button
            className='bulk-status-btn'
            onClick={() => setShowBulkDropdown((prev) => !prev)}
          >
            Bulk Status Update
          </button>
        </div>
        <div className='Headerstyle'>
          {getStatusEnums().map((enumVal) => (
            <button
              key={enumVal}
              className={
                "status-tab-btn " +
                (activeStatusTab === enumVal ? "active-status-tab" : "")
              }
              onClick={() => handleStatusTabClick(enumVal)}
            >
              {statusLabel(enumVal)}
            </button>
          ))}
        </div>
        <div className='filter-right-group'>
          {showBulkDropdown && (
            <div className='bulk-status-popup'>
              <select
                className='bulk-status-select'
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
              >
                <option value=''>Select Status</option>
                {getStatusEnums().map((ev) => (
                  <option key={ev} value={ev}>
                    {statusLabel(ev)}
                  </option>
                ))}
              </select>
              <button
                className='bulk-status-apply-btn'
                onClick={handleBulkStatusUpdate}
              >
                Apply
              </button>
            </div>
          )}
          <button
            className='filter-btn'
            onClick={() => setShowFilterModal(true)}
          >
            <span className='filter-icon'><BsSliders /></span> Filter
          </button>
        </div>
      </div>

      {/* Candidate table */}
      <div className='table-wrapper'>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 24, textAlign: "center", color: "crimson" }}>
            {error}
          </div>
        ) : !selectedClient ? (
          <div style={{ padding: 24, textAlign: "center", fontSize: '12px' }}>
            Please select a client to view candidates.
          </div>
        ) : (
          <table className='candidate-table'>
            <thead>
              <tr>
                {showBulkDropdown && (
                  <th>
                    <input
                      type='checkbox'
                      checked={selectedRows.length === pageRows.length && pageRows.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th>Candidate Name</th>
                <th>Email</th>
                <th>Job Role</th>
                <th>Created Date</th>
                <th>Candidate Key</th>
                <th className='status-col'>Status</th>
              </tr>
            </thead>

            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 24 }}>
                    {`No records for "${selectedClient}" in "${statusLabel(
                      activeStatusTab
                    )}" status`}
                  </td>
                </tr>
              ) : (
                pageRows.map((c) => (
                  <tr key={c.key} className={selectedRows.includes(c.key) ? 'row-selected' : ''}>
                    {showBulkDropdown && (
                      <td>
                        <input
                          type='checkbox'
                          checked={selectedRows.includes(c.key)}
                          onChange={() => toggleRowSelection(c.key)}
                        />
                      </td>
                    )}
                    <td className="ellipsis" title={c.name}>
                      {truncateText(c.name, 12)}
                    </td>
                    <td>{c.email}</td>
                    <td>{c.role}</td>
                    <td>{c.createdDate}</td>
                    <td>{c.key}</td>
                    <td className='status-col'>
                      <select
                        className="row-status-select"
                        value={c.status}
                        onChange={(e) => updateCandidateStatus(c.key, e.target.value)}
                      >
                        {getStatusEnums().map((ev) => (
                          <option key={ev} value={ev}>
                            {statusLabel(ev)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer / pagination */}
      <div className='pagination-row'>
        <div className='data-info'>
          {loading
            ? "Loading…"
            : !selectedClient
              ? "Select a client to view data"
              : totalRows === 0
                ? "Showing data 0 of 0 entries"
                : `Showing data ${showingFrom} to ${showingTo} of ${totalRows} entries`}
        </div>

        <div className='pager'>
          {/* Prev */}
          <button
            className='page-btn'
            disabled={currentPage === 1 || totalRows === 0 || !selectedClient}
            onClick={() => goToPage(currentPage - 1)}
          >
            &lt;
          </button>

          {/* Page numbers */}
          {selectedClient &&
            getPageNumbers().map((p, i) => {
              if (p === "dots-left" || p === "dots-right") {
                return (
                  <button
                    key={p + i}
                    className='page-btn'
                    disabled
                    style={{ cursor: "default" }}
                  >
                    …
                  </button>
                );
              }
              return (
                <button
                  key={p}
                  className={
                    "page-btn " + (currentPage === p ? "active-page" : "")
                  }
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              );
            })}

          {/* Next */}
          <button
            className='page-btn'
            disabled={
              currentPage === totalPages || totalRows === 0 || !selectedClient
            }
            onClick={() => goToPage(currentPage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Filter Modal (unchanged UI) */}
      {showFilterModal && (
        <div className='filter-modal-backdrop'>
          <div className='filter-modal'>
            <h3 className='filter-modal-title'>Filter</h3>

            <div className='filter-field'>
              <label className='filter-label'>Recruitment Name</label>
              <select
                name="recruitmentId"
                className='filter-select'
                value={filterRecruitmentId}
                onChange={(e) => setFilterRecruitmentId(e.target.value)}
              >
                <option value="">Select Recruitment</option>
                {recruitmentOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className='filter-field'>
              <label className='filter-label'>Sort By</label>
              <select
                className='filter-select'
                value={filterSortBy}
                onChange={(e) => setFilterSortBy(e.target.value)}
              >
                <option value=''></option>
                <option value='name-asc'>Name (A-Z)</option>
                <option value='name-desc'>Name (Z-A)</option>
                <option value='date-asc'>Date (Oldest First)</option>
                <option value='date-desc'>Date (Newest First)</option>
              </select>
            </div>

            <div className='filter-modal-buttons'>
              <button className='filter-clear-btn' onClick={handleClearFilter}>
                Clear
              </button>
              <button className='filter-apply-btn' onClick={handleApplyFilter}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecruitmentPipeline;
